import {
	describeFormFieldError,
	FORM_FIELD_DEFAULT_MAX_LENGTH,
	formatAgreementDateTime,
	formatAgreementYmd,
	isFormTermsUrl,
	validateFormAnswers,
	type FormDefinitionModel,
	type FormFieldDefinitionModel,
	type FormFieldErrorCode,
	type FormFieldIdentity,
	type FormLayoutColumnCount,
	type FormValidationResultModel,
} from '@supply-mover-2-0/shared';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { Callout } from '../../feedback/Callout';
import { IssueRichTextContent } from '../../issues/IssueRichTextContent';
import { Modal } from '../../overlays/Modal';
import { Checkbox } from '../Checkbox';
import { FieldValueInput } from '../FieldValueInput';
import { FormField } from '../FormField';
import { ReadOnlyFieldValue } from '../ReadOnlyFieldValue';
import { SignaturePad } from '../SignaturePad';
import { TypedSignature } from '../TypedSignature';
import { FormFileQuestion } from './FormFileQuestion';
import { FormTableQuestion, type FormTableLookupsModel } from './FormTableQuestion';
import {
	FORM_FILE_UPLOAD_FAILED_MESSAGE,
	formFileAnswer,
	initialFormValues,
	inputMaxLength,
	pendingFormUploads,
	planFormFileAdditions,
	resolveLayout,
	visibleFormErrors,
	type FormFileEntryModel,
	type FormServerErrorsModel,
	type FormVisibleErrorsModel,
	type ResolvedFormTextCellModel,
} from './formRendererState';

/**
 * PD-16596 — the Workd Forms renderer (docs/claude/forms/01-design.md §12): draws a form
 * from its definition and checks it with the shared validator as the visitor goes, so the
 * storefront Register page, the Forms settings preview and later staff entry look and
 * validate the same way, and say the same thing the server would.
 *
 * It uses only the shared token classes (text-default, border-default, …), which the CRM
 * theme and the storefront's brand theme both provide, as literal class names: the
 * storefront builds its styles by scanning this library's source.
 *
 * PD-16687: `definition.layout` arranges the questions into titled sections and rows of
 * one to three columns, with text between them (resolveLayout); without one, each
 * question has its own row in field order.
 *
 * PD-16730: a question with a Show only when rule is drawn only while the earlier
 * question has one of the chosen answers (the shared validator's `hiddenKeys`); rows and
 * sections with nothing left to show are not drawn.
 *
 * PD-16713: a file question uploads each file as it is picked, through the page's
 * `uploadFile` (one at a time, in the order picked), and its answer is the upload ids of
 * the files Workd has. Without `uploadFile` (the settings Preview) a picked file is checked
 * on the page and nothing is sent. A page must not submit while `pendingUploads` is above
 * zero: a file still on its way is not part of the answer yet.
 *
 * Usage:
 *   const form = useFormRenderer(definition, undefined, { uploadFile });
 *   <FormRenderer definition={definition} state={form} idPrefix="contact" />
 *   onSubmit: const result = form.attempt(); if (result.valid) send(result.answers);
 *             on a 400, form.setServerErrors(body)
 */

/** PD-16713 — one file for the page to upload for a file question. */
export interface FormFileUploadInputModel {
	fieldKey: string;
	/** The page's id for the upload; the answer names it once Workd has the file. */
	uploadId: string;
	file: File;
}

/**
 * What became of one upload: Workd's name and size for the file it kept, or why it was
 * refused (a field error code, or the server's own message; neither for a lost connection).
 */
export type FormFileUploadResultModel =
	| { ok: true; name: string; sizeBytes: number }
	| { ok: false; code: FormFieldErrorCode | null; message: string | null };

export type FormFileUploaderModel = (
	input: FormFileUploadInputModel,
) => Promise<FormFileUploadResultModel>;

export interface FormRendererOptionsModel {
	/**
	 * Sends one file to Workd (the public uploads endpoint). Without it, a file that passes
	 * the page's checks is kept on the page only, as the settings Preview wants.
	 */
	uploadFile?: FormFileUploaderModel;
}

export interface FormRendererStateModel {
	/** The answers so far; a file question's is the upload ids of the files Workd has. */
	values: Record<string, unknown>;
	setValue: (fieldKey: string, value: unknown) => void;
	/** Marks a field as left, so its own error shows. */
	touch: (fieldKey: string) => void;
	/** Marks a submit attempt and returns the validation (answers cleaned when valid). */
	attempt: () => FormValidationResultModel;
	validation: FormValidationResultModel;
	visible: FormVisibleErrorsModel;
	setServerErrors: (errors: FormServerErrorsModel | null) => void;
	reset: () => void;
	/** PD-16713 — each file question's files, in the order picked. */
	files: Record<string, Array<FormFileEntryModel>>;
	/** Checks the picked files and uploads those that pass, one at a time. */
	addFiles: (fieldKey: string, files: Array<File>) => void;
	/** Takes a file off the question (an upload still on its way is left unattached). */
	removeFile: (fieldKey: string, uploadId: string) => void;
	/** Files across the form still on their way; the page waits for none before sending. */
	pendingUploads: number;
	/**
	 * PD-13768 — how each signature question was signed (drawn, or typed and set in a
	 * script face), for the signing record. Drawn unless the signer switched.
	 */
	signatureModes: Record<string, 'draw' | 'type'>;
	setSignatureMode: (fieldKey: string, mode: 'draw' | 'type') => void;
	/** PD-13768 — the name typed for each typed signature. */
	typedNames: Record<string, string>;
	setTypedName: (fieldKey: string, name: string) => void;
}

type FormFilesModel = Record<string, Array<FormFileEntryModel>>;

export function useFormRenderer(
	definition: FormDefinitionModel,
	initial?: Record<string, unknown>,
	options: FormRendererOptionsModel = {},
): FormRendererStateModel {
	const start = useCallback(
		() => ({ ...initialFormValues(definition.fields), ...(initial ?? {}) }),
		[definition.fields, initial],
	);
	const [values, setValues] = useState<Record<string, unknown>>(start);
	const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
	const [attempted, setAttempted] = useState(false);
	const [server, setServer] = useState<FormServerErrorsModel | null>(null);
	const [signatureModes, setSignatureModes] = useState<Record<string, 'draw' | 'type'>>({});
	const [typedNames, setTypedNames] = useState<Record<string, string>>({});
	const setSignatureMode = useCallback((fieldKey: string, mode: 'draw' | 'type'): void => {
		setSignatureModes((previous) => ({ ...previous, [fieldKey]: mode }));
	}, []);
	const setTypedName = useCallback((fieldKey: string, name: string): void => {
		setTypedNames((previous) => ({ ...previous, [fieldKey]: name }));
	}, []);

	// PD-16713 — file questions. The entries live in a ref as well as in state, so two quick
	// picks, and the upload loop, always work from the latest list rather than from the
	// last render's.
	const [files, setFiles] = useState<FormFilesModel>({});
	const filesRef = useRef<FormFilesModel>({});
	const queueRef = useRef<Array<FormFileUploadInputModel>>([]);
	const uploadingRef = useRef(false);
	const removedRef = useRef<Set<string>>(new Set());
	const uploaderRef = useRef(options.uploadFile);
	uploaderRef.current = options.uploadFile;
	const fieldsRef = useRef(definition.fields);
	fieldsRef.current = definition.fields;

	// What the validator reads: the typed answers, and for each file question the files
	// Workd has.
	const answers = useMemo(() => {
		const merged: Record<string, unknown> = { ...values };
		for (const field of definition.fields) {
			if (field.fieldType === 'file') {
				merged[field.fieldKey] = formFileAnswer(files[field.fieldKey] ?? []);
			}
		}
		return merged;
	}, [definition.fields, values, files]);

	const validation = useMemo(
		() => validateFormAnswers(definition, answers),
		[definition, answers],
	);
	const visible = useMemo(
		() =>
			visibleFormErrors(definition, validation, { touched, attempted }, server),
		[definition, validation, touched, attempted, server],
	);

	// The server's word on a field no longer holds once the visitor changes it.
	const clearServerError = useCallback((fieldKey: string): void => {
		setServer((previous) => {
			if (previous?.errors?.[fieldKey] == null) {
				return previous;
			}
			const errors = { ...previous.errors };
			delete errors[fieldKey];
			return { ...previous, errors };
		});
	}, []);

	const setValue = useCallback(
		(fieldKey: string, value: unknown): void => {
			setValues((previous) => ({ ...previous, [fieldKey]: value }));
			clearServerError(fieldKey);
		},
		[clearServerError],
	);

	const touch = useCallback((fieldKey: string): void => {
		setTouched((previous) =>
			previous.has(fieldKey) ? previous : new Set([...previous, fieldKey]),
		);
	}, []);

	const commitFiles = useCallback((next: FormFilesModel): void => {
		filesRef.current = next;
		setFiles(next);
	}, []);

	const updateEntry = useCallback(
		(
			fieldKey: string,
			uploadId: string,
			change: Partial<FormFileEntryModel>,
		): void => {
			const current = filesRef.current;
			commitFiles({
				...current,
				[fieldKey]: (current[fieldKey] ?? []).map((entry) =>
					entry.uploadId === uploadId ? { ...entry, ...change } : entry,
				),
			});
		},
		[commitFiles],
	);

	// One upload at a time, in the order picked: the first upload of a form fill opens its
	// upload session with the page's one spam-check token, which the rest then ride on.
	const runUploads = useCallback(async (): Promise<void> => {
		if (uploadingRef.current) {
			return;
		}
		uploadingRef.current = true;
		try {
			for (
				let job = queueRef.current.shift();
				job != null;
				job = queueRef.current.shift()
			) {
				if (removedRef.current.has(job.uploadId)) {
					continue;
				}
				updateEntry(job.fieldKey, job.uploadId, { status: 'uploading' });
				const upload = uploaderRef.current;
				let result: FormFileUploadResultModel;
				if (upload == null) {
					result = { ok: true, name: job.file.name, sizeBytes: job.file.size };
				} else {
					try {
						result = await upload(job);
					} catch {
						result = { ok: false, code: null, message: null };
					}
				}
				// Removed while it uploaded: it stays off the question (Workd deletes it later).
				if (removedRef.current.has(job.uploadId)) {
					continue;
				}
				const fieldKey = job.fieldKey;
				const field = fieldsRef.current.find(
					(candidate) => candidate.fieldKey === fieldKey,
				);
				updateEntry(
					fieldKey,
					job.uploadId,
					result.ok
						? {
								status: 'done',
								name: result.name,
								sizeBytes: result.sizeBytes,
								error: null,
							}
						: {
								status: 'refused',
								error:
									result.message ??
									(result.code != null && field != null
										? describeFormFieldError(field, result.code)
										: FORM_FILE_UPLOAD_FAILED_MESSAGE),
							},
				);
			}
		} finally {
			uploadingRef.current = false;
		}
	}, [updateEntry]);

	const addFiles = useCallback(
		(fieldKey: string, picked: Array<File>): void => {
			const field = fieldsRef.current.find(
				(candidate) => candidate.fieldKey === fieldKey,
			);
			if (field == null || picked.length === 0) {
				return;
			}
			const current = filesRef.current[fieldKey] ?? [];
			const planned = planFormFileAdditions(field, current, picked, () =>
				crypto.randomUUID(),
			);
			commitFiles({
				...filesRef.current,
				[fieldKey]: [...current, ...planned],
			});
			planned.forEach((entry, index) => {
				const file = picked[index];
				if (entry.status === 'queued' && file != null) {
					queueRef.current.push({ fieldKey, uploadId: entry.uploadId, file });
				}
			});
			touch(fieldKey);
			clearServerError(fieldKey);
			void runUploads();
		},
		[clearServerError, commitFiles, runUploads, touch],
	);

	const removeFile = useCallback(
		(fieldKey: string, uploadId: string): void => {
			removedRef.current.add(uploadId);
			commitFiles({
				...filesRef.current,
				[fieldKey]: (filesRef.current[fieldKey] ?? []).filter(
					(entry) => entry.uploadId !== uploadId,
				),
			});
			touch(fieldKey);
			clearServerError(fieldKey);
		},
		[clearServerError, commitFiles, touch],
	);

	const attempt = useCallback((): FormValidationResultModel => {
		setAttempted(true);
		setServer((previous) =>
			previous == null ? null : { ...previous, formErrors: [] },
		);
		return validateFormAnswers(definition, answers);
	}, [definition, answers]);

	const reset = useCallback((): void => {
		setValues(start());
		setTouched(new Set());
		setAttempted(false);
		setServer(null);
		// Uploads still waiting or on their way are left unattached.
		for (const entry of Object.values(filesRef.current).flat()) {
			removedRef.current.add(entry.uploadId);
		}
		queueRef.current = [];
		commitFiles({});
	}, [commitFiles, start]);

	return {
		values: answers,
		setValue,
		touch,
		attempt,
		validation,
		visible,
		setServerErrors: setServer,
		reset,
		files,
		addFiles,
		removeFile,
		pendingUploads: pendingFormUploads(files),
		signatureModes,
		setSignatureMode,
		typedNames,
		setTypedName,
	};
}

interface FormRendererProps {
	definition: FormDefinitionModel;
	state: FormRendererStateModel;
	/** Keeps field ids unique when two forms share a page. */
	idPrefix: string;
	disabled?: boolean;
	className?: string;
	/**
	 * Draw every row, and every list of choices, as one column, as a phone does. The
	 * Layout tab's phone preview uses it: the page's breakpoints follow the window, not
	 * the width of the preview.
	 */
	singleColumn?: boolean;
	/**
	 * PD-13768 — sent forms: questions shown read-only (prefilled and locked, filled by
	 * staff). Their value is drawn as text; the server keeps its own value whatever the
	 * page holds.
	 */
	readOnlyKeys?: ReadonlySet<string>;
	/** PD-13768 — table questions the person may edit (staff before sending). */
	editableTableKeys?: ReadonlySet<string>;
	/** PD-13768 — names for table cells (items, models). */
	lookups?: FormTableLookupsModel | null;
	/**
	 * PD-13768 — a page's own drawing of a question the renderer cannot draw by itself
	 * (the storefront's payment method question, with its card form). Null falls back to
	 * the renderer.
	 */
	renderCustomField?: (field: FormFieldDefinitionModel) => React.ReactElement | null;
}

/** Browser autofill hints from what a field says about the submitter. */
const AUTOCOMPLETE: Readonly<Partial<Record<FormFieldIdentity, string>>> = {
	first_name: 'given-name',
	last_name: 'family-name',
	full_name: 'name',
	email: 'email',
	phone: 'tel',
	company: 'organization',
};

/**
 * A multi-choice question with this many choices or fewer is drawn as a list of
 * checkboxes: every choice is visible at once, which reads better on a public form than a
 * dropdown (the storefront's registration form drew them this way before Forms). Longer
 * lists stay a searchable dropdown, unless the layout gives the question choice columns.
 */
const CHECKBOX_LIST_MAX_CHOICES = 8;

/**
 * PD-16687 — layout grids, as literal class names because the storefront builds its
 * styles by scanning this source. Every row is one column at phone width.
 */
const ROW_GRID: Readonly<Record<FormLayoutColumnCount, string>> = {
	1: 'grid grid-cols-1 gap-x-6 gap-y-5',
	2: 'grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2',
	3: 'grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-3',
};

const CELL_SPAN: Readonly<Record<FormLayoutColumnCount, string>> = {
	1: 'min-w-0',
	2: 'min-w-0 sm:col-span-2',
	3: 'min-w-0 sm:col-span-3',
};

const CHOICE_GRID: Readonly<Record<FormLayoutColumnCount, string>> = {
	1: 'grid grid-cols-1 gap-x-4 gap-y-2',
	2: 'grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2',
	3: 'grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3',
};

interface ChoiceCheckboxesProps {
	label: string;
	options: ReadonlyArray<{ value: string; label: string }>;
	value: unknown;
	onChange: (value: Array<string>) => void;
	onBlur: () => void;
	disabled: boolean;
	columns: FormLayoutColumnCount;
	// Injected by FormField: the id the error summary focuses, and the error wiring.
	id?: string;
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
}

function ChoiceCheckboxes({
	label,
	options,
	value,
	onChange,
	onBlur,
	disabled,
	columns,
	id,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: ChoiceCheckboxesProps): React.ReactElement {
	const { t } = useTranslation();
	const selected = new Set(
		Array.isArray(value)
			? value.filter((choice): choice is string => typeof choice === 'string')
			: [],
	);
	return (
		<div
			id={id}
			role="group"
			aria-label={t(label)}
			aria-invalid={ariaInvalid}
			aria-describedby={ariaDescribedBy}
			// Focusable so the error summary's link can move to the question.
			tabIndex={-1}
			className={cn(CHOICE_GRID[columns], 'pt-1 outline-none')}
		>
			{options.map((option) => (
				<Checkbox
					key={option.value}
					id={id != null ? `${id}-${option.value}` : undefined}
					checked={selected.has(option.value)}
					onChange={(checked) => {
						const next = new Set(selected);
						if (checked) {
							next.add(option.value);
						} else {
							next.delete(option.value);
						}
						// Answers keep the order the choices are listed in.
						onChange(
							options
								.filter((choice) => next.has(choice.value))
								.map((choice) => choice.value),
						);
						onBlur();
					}}
					label={t(option.label)}
					disabled={disabled}
				/>
			))}
		</div>
	);
}

interface ChoiceRadiosProps {
	label: string;
	/** Groups the radio buttons; unique on the page. */
	name: string;
	options: ReadonlyArray<{ value: string; label: string }>;
	value: unknown;
	onChange: (value: string | null) => void;
	onBlur: () => void;
	disabled: boolean;
	columns: FormLayoutColumnCount;
	/**
	 * An optional question: once a choice is made it can be taken back. A radio button
	 * cannot be un-chosen, and a choice made by mistake on an account manager question
	 * would otherwise make that person the new lead's sales rep.
	 */
	canClear: boolean;
	// Injected by FormField: the id the error summary focuses, and the error wiring.
	id?: string;
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
	'aria-required'?: boolean;
}

/**
 * PD-16687 — a single-answer question listed as radio buttons, drawn when the layout
 * gives the question choice columns (otherwise it stays a dropdown). The answer is the
 * chosen value, exactly as the dropdown stores it.
 */
function ChoiceRadios({
	label,
	name,
	options,
	value,
	onChange,
	onBlur,
	disabled,
	columns,
	canClear,
	id,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
	'aria-required': ariaRequired,
}: ChoiceRadiosProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const chosen = typeof value === 'string' ? value : null;
	return (
		<div
			id={id}
			role="radiogroup"
			aria-label={t(label)}
			aria-invalid={ariaInvalid}
			aria-describedby={ariaDescribedBy}
			aria-required={ariaRequired}
			// Focusable so the error summary's link can move to the question.
			tabIndex={-1}
			className={cn(CHOICE_GRID[columns], 'pt-1 outline-none')}
		>
			{options.map((option, index) => {
				// By position: an option value may hold characters an id cannot.
				const optionId = id != null ? `${id}-option-${index}` : undefined;
				return (
					<div key={option.value} className="flex items-start">
						<div className="flex h-5 items-center">
							<input
								type="radio"
								id={optionId}
								name={name}
								value={option.value}
								checked={chosen === option.value}
								onChange={() => {
									onChange(option.value);
									onBlur();
								}}
								disabled={disabled}
								className={cn(
									// Painted from the theme tokens by form-controls.css, as the
									// checkboxes are.
									'h-4 w-4',
									colors.classes.focusRingPrimary,
									ariaInvalid === true && 'error',
									disabled && 'cursor-not-allowed opacity-50',
								)}
							/>
						</div>
						<label
							htmlFor={optionId}
							className={cn(
								'ml-3 cursor-pointer text-sm font-medium',
								colors.classes.textPrimary,
								disabled && 'cursor-not-allowed opacity-50',
							)}
						>
							{t(option.label)}
						</label>
					</div>
				);
			})}
			{canClear && chosen != null && chosen !== '' && (
				<button
					type="button"
					className={cn(
						'col-span-full justify-self-start text-sm underline',
						colors.classes.textSecondary,
					)}
					disabled={disabled}
					onClick={() => {
						onChange(null);
						onBlur();
					}}
				>
					{t('Clear choice')}
				</button>
			)}
		</div>
	);
}

export function FormRenderer({
	definition,
	state,
	idPrefix,
	disabled = false,
	className,
	singleColumn = false,
	readOnlyKeys,
	editableTableKeys,
	lookups = null,
	renderCustomField,
}: FormRendererProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	// PD-13768 — the terms a "Read the terms" link opened, by field key.
	const [termsOpen, setTermsOpen] = useState<string | null>(null);
	// PD-16730 — the questions their Show only when rules hide for the answers so far.
	const hiddenKeys = state.validation.hiddenKeys;
	const sections = useMemo(
		() => resolveLayout(definition, hiddenKeys),
		[definition, hiddenKeys],
	);
	const fieldName = (field: FormFieldDefinitionModel): string =>
		`${idPrefix}-${field.fieldKey}`;

	const renderField = (
		field: FormFieldDefinitionModel,
		optionColumns: FormLayoutColumnCount | null,
		partWidth: boolean,
	): React.ReactElement => {
		const error = state.visible.fields[field.fieldKey];
		const ruleMessages = state.visible.rules[field.fieldKey] ?? [];
		const rules = ruleMessages.map((message) => (
			<p key={message} className={cn('text-sm', colors.classes.textDanger)}>
				{t(message)}
			</p>
		));

		// PD-13768 — a page's own drawing of the question (a payment method).
		const custom = renderCustomField?.(field) ?? null;
		if (custom != null) {
			return custom;
		}

		// PD-13768 — a table question: rows staff fill, read-only for everyone else.
		if (field.fieldType === 'table') {
			return (
				<FormField
					name={fieldName(field)}
					label={t(field.label)}
					required={field.isRequired && editableTableKeys?.has(field.fieldKey) === true}
					help={field.helpText != null ? t(field.helpText) : undefined}
					error={error != null ? t(error) : undefined}
				>
					<FormTableQuestion
						field={field}
						value={state.values[field.fieldKey]}
						editable={editableTableKeys?.has(field.fieldKey) === true}
						lookups={lookups}
						onChange={(rows) => {
							state.setValue(field.fieldKey, rows);
							state.touch(field.fieldKey);
						}}
						disabled={disabled}
					/>
					{rules}
				</FormField>
			);
		}

		// PD-13768 — a value the person may read but not change (prefilled and locked, or
		// set by staff before the form was sent).
		if (readOnlyKeys?.has(field.fieldKey) === true && field.fieldType !== 'consent') {
			return (
				<FormField name={fieldName(field)} label={t(field.label)} help={field.helpText != null ? t(field.helpText) : undefined}>
					<ReadOnlyFieldValue>
						<span className="whitespace-pre-line">
							{t(readOnlyText(field, state.values[field.fieldKey]) || '—')}
						</span>
					</ReadOnlyFieldValue>
				</FormField>
			);
		}

		if (field.fieldType === 'consent') {
			const errorId = `field-${fieldName(field)}-error`;
			const termsId = `field-${fieldName(field)}-terms`;
			// PD-16691 — the terms the checkbox agrees to. A version saved before
			// consent terms existed has no such keys, hence `?? null`. The link is
			// checked again here, as the body is sanitized again by the renderer, so a
			// value that ever bypassed the server rule is still not drawn.
			const termsBody = field.termsBody ?? null;
			// PD-13768 — long terms open in a dialog from a link instead of a scrolling box.
			const termsInDialog = termsBody != null && field.fieldOptions?.termsDisplay === 'dialog';
			const termsUrl =
				field.termsUrl != null && isFormTermsUrl(field.termsUrl)
					? field.termsUrl
					: null;
			const describedBy = [
				error != null ? errorId : null,
				termsBody != null ? termsId : null,
			]
				.filter((id): id is string => id != null)
				.join(' ');
			return (
				<div className="space-y-1">
					<Checkbox
						id={`field-${fieldName(field)}`}
						checked={state.values[field.fieldKey] === true}
						onChange={(checked) => {
							state.setValue(field.fieldKey, checked);
							state.touch(field.fieldKey);
						}}
						label={t(field.label)}
						help={field.helpText != null ? t(field.helpText) : undefined}
						error={error != null}
						aria-invalid={error != null}
						aria-describedby={describedBy !== '' ? describedBy : undefined}
						aria-required={field.isRequired}
						disabled={disabled}
					/>
					{termsInDialog && (
						<p className="ml-7 text-sm">
							<button
								type="button"
								className="text-accent underline hover:text-accent-hover"
								onClick={() => {
									setTermsOpen(field.fieldKey);
								}}
							>
								{t(`Read the ${field.adminLabel ?? 'terms'}`)}
							</button>
						</p>
					)}
					{termsInDialog && (
						<Modal
							isOpen={termsOpen === field.fieldKey}
							onClose={() => {
								setTermsOpen(null);
							}}
							title={t(field.adminLabel ?? 'Terms and conditions')}
							size="xl"
							actions={[
								{
									label: t('Close'),
									onClick: () => {
										setTermsOpen(null);
									},
								},
							]}
						>
							<div id={termsId} className="max-h-[60vh] overflow-y-auto text-sm text-default">
								<IssueRichTextContent html={termsBody} />
							</div>
						</Modal>
					)}
					{termsBody != null && !termsInDialog && (
						// Focusable so keyboard users can reach it and scroll it with the
						// arrow keys; a named region so a screen reader announces it.
						<div
							id={termsId}
							role="region"
							aria-label={t('Terms and conditions')}
							tabIndex={0}
							className="max-h-52 overflow-y-auto rounded-md border border-default bg-surface px-3 py-2 text-sm text-default focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-0"
						>
							<IssueRichTextContent html={termsBody} />
						</div>
					)}
					{termsUrl != null && (
						<p className="text-sm">
							<a
								href={termsUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="text-accent underline hover:text-accent-hover"
							>
								{field.termsLinkText != null
									? t(field.termsLinkText)
									: t('Read the full terms')}
							</a>
						</p>
					)}
					{error != null && (
						<p
							id={errorId}
							role="alert"
							className={cn('text-sm', colors.classes.textDanger)}
						>
							{t(error)}
						</p>
					)}
					{rules}
				</div>
			);
		}

		if (field.fieldType === 'signature') {
			// PD-16691 — drawn on the shared pad (white paper, dark ink in every theme). It
			// fills its cell, full or part width, and never scrolls the page while signing.
			const signature = state.values[field.fieldKey];
			// PD-13768 — a signature that may be typed instead of drawn offers both.
			const modes = field.fieldOptions?.signatureModes ?? ['draw'];
			const mode = state.signatureModes[field.fieldKey] ?? (modes[0] ?? 'draw');
			const modeSwitch = modes.length > 1 && (
				<div className="mb-2 inline-flex rounded-md border border-default text-sm" role="group" aria-label={t('How to sign')}>
					{modes.map((option) => (
						<button
							key={option}
							type="button"
							aria-pressed={mode === option}
							className={cn(
								'px-3 py-1.5',
								mode === option ? 'bg-accent text-white' : colors.classes.textPrimary,
							)}
							onClick={() => {
								if (option !== mode) {
									state.setSignatureMode(field.fieldKey, option);
									state.setValue(field.fieldKey, null);
								}
							}}
							disabled={disabled}
						>
							{option === 'draw' ? t('Draw') : t('Type')}
						</button>
					))}
				</div>
			);
			if (mode === 'type') {
				return (
					<FormField
						name={fieldName(field)}
						label={t(field.label)}
						required={field.isRequired}
						help={field.helpText != null ? t(field.helpText) : undefined}
						error={error != null ? t(error) : undefined}
					>
						<div>
							{modeSwitch}
							<TypedSignature
								name={state.typedNames[field.fieldKey] ?? ''}
								label={field.label}
								disabled={disabled}
								onChange={(name, image) => {
									state.setTypedName(field.fieldKey, name);
									state.setValue(field.fieldKey, image);
								}}
								onBlur={() => {
									state.touch(field.fieldKey);
								}}
							/>
						</div>
						{rules}
					</FormField>
				);
			}
			return (
				<FormField
					name={fieldName(field)}
					label={t(field.label)}
					required={field.isRequired}
					help={field.helpText != null ? t(field.helpText) : undefined}
					error={error != null ? t(error) : undefined}
				>
					{modeSwitch}
					<SignaturePad
						label={field.label}
						value={typeof signature === 'string' ? signature : null}
						onChange={(value) => {
							state.setValue(field.fieldKey, value);
						}}
						onBlur={() => {
							state.touch(field.fieldKey);
						}}
						height={160}
						disabled={disabled}
					/>
					{rules}
				</FormField>
			);
		}

		if (field.fieldType === 'file') {
			// PD-16713 — a single-row drop zone when the question shares its row.
			return (
				<FormField
					name={fieldName(field)}
					label={t(field.label)}
					required={field.isRequired}
					help={field.helpText != null ? t(field.helpText) : undefined}
					error={error != null ? t(error) : undefined}
				>
					<FormFileQuestion
						field={field}
						entries={state.files[field.fieldKey] ?? []}
						onAdd={(picked) => {
							state.addFiles(field.fieldKey, picked);
						}}
						onRemove={(uploadId) => {
							state.removeFile(field.fieldKey, uploadId);
						}}
						disabled={disabled}
						compact={partWidth}
					/>
					{rules}
				</FormField>
			);
		}

		// PD-16691 — an account manager question is a single choice among the people the
		// server listed in its selectOptions, drawn exactly as a dropdown question is.
		const inputType =
			field.fieldType === 'teamMember' ? 'select' : field.fieldType;

		// Choices are listed (not a dropdown) when the layout gives the question columns,
		// or, for a multiple-answer question, when its choices are few enough to show.
		const choices = field.selectOptions ?? [];
		const listColumns: FormLayoutColumnCount | null =
			optionColumns ??
			(field.fieldType === 'multiselect' &&
			choices.length <= CHECKBOX_LIST_MAX_CHOICES
				? 1
				: null);
		if (
			(inputType === 'multiselect' || inputType === 'select') &&
			choices.length > 0 &&
			listColumns != null
		) {
			const columns = singleColumn ? 1 : listColumns;
			return (
				<FormField
					name={fieldName(field)}
					label={t(field.label)}
					required={field.isRequired}
					help={field.helpText != null ? t(field.helpText) : undefined}
					error={error != null ? t(error) : undefined}
				>
					{field.fieldType === 'multiselect' ? (
						<ChoiceCheckboxes
							label={field.label}
							options={choices}
							value={state.values[field.fieldKey]}
							onChange={(value) => {
								state.setValue(field.fieldKey, value);
							}}
							onBlur={() => {
								state.touch(field.fieldKey);
							}}
							disabled={disabled}
							columns={columns}
						/>
					) : (
						<ChoiceRadios
							label={field.label}
							name={fieldName(field)}
							options={choices}
							value={state.values[field.fieldKey]}
							onChange={(value) => {
								state.setValue(field.fieldKey, value);
							}}
							onBlur={() => {
								state.touch(field.fieldKey);
							}}
							disabled={disabled}
							columns={columns}
							canClear={!field.isRequired}
						/>
					)}
					{rules}
				</FormField>
			);
		}

		const min = field.validationRules?.min;
		const max = field.validationRules?.max;
		return (
			<FormField
				name={fieldName(field)}
				label={t(field.label)}
				required={field.isRequired}
				help={field.helpText != null ? t(field.helpText) : undefined}
				error={error != null ? t(error) : undefined}
			>
				<FieldValueInput
					fieldType={inputType}
					name={field.fieldKey}
					label={field.label}
					value={state.values[field.fieldKey]}
					onChange={(value) => {
						state.setValue(field.fieldKey, value);
					}}
					onBlur={() => {
						state.touch(field.fieldKey);
					}}
					selectOptions={field.selectOptions}
					placeholder={field.placeholder}
					maxLength={inputMaxLength(field, FORM_FIELD_DEFAULT_MAX_LENGTH)}
					min={typeof min === 'number' ? min : undefined}
					max={typeof max === 'number' ? max : undefined}
					autoComplete={
						field.identity != null ? AUTOCOMPLETE[field.identity] : undefined
					}
					emptyMoneyIsNull
					genericPlaceholders={false}
					disabled={disabled}
				/>
				{rules}
			</FormField>
		);
	};

	const renderText = (cell: ResolvedFormTextCellModel): React.ReactElement =>
		cell.style === 'heading' ? (
			<h3 className={cn('text-base font-semibold', colors.classes.textPrimary)}>
				{t(cell.text)}
			</h3>
		) : (
			// Line breaks the admin typed are kept, so terms read as they were written.
			<p
				className={cn(
					'whitespace-pre-line text-sm',
					colors.classes.textPrimary,
				)}
			>
				{t(cell.text)}
			</p>
		);

	return (
		<div className={cn('space-y-5', className)}>
			{state.visible.summary.length > 0 && (
				<Callout variant="danger" title={t('Please check the following')}>
					<ul className="list-disc pl-5 space-y-1 text-sm">
						{state.visible.summary.map((item, index) => (
							<li key={`${item.fieldKey ?? 'form'}-${index}`}>
								{item.fieldKey != null ? (
									<a
										href={`#field-${idPrefix}-${item.fieldKey}`}
										className="underline"
										onClick={(event) => {
											event.preventDefault();
											document
												.getElementById(
													`field-${idPrefix}-${item.fieldKey ?? ''}`,
												)
												?.focus();
										}}
									>
										{t(item.message)}
									</a>
								) : (
									t(item.message)
								)}
							</li>
						))}
					</ul>
				</Callout>
			)}

			{sections.map((section, sectionIndex) => {
				const hasHeader = section.title != null || section.description != null;
				const headingId = `${idPrefix}-section-${section.id}`;
				const rows = section.rows.map((row) => (
					<div
						key={row.id}
						className={singleColumn ? ROW_GRID[1] : ROW_GRID[row.columns]}
					>
						{/* Cells in page order, so reading and tab order follow what is seen. */}
						{row.cells.map((cell) =>
							cell.kind === 'field' && cell.hidden ? (
								// PD-16730 — a question hidden by its Show only when rule keeps its
								// column on a wide screen when it shares the row, so the one beside
								// it does not move when it appears; at phone width (one column), or
								// when it fills the row, it takes no room. What was typed in it stays
								// in the form's values and comes back with it; the validator leaves
								// it out of what is sent.
								<div
									key={`field:${cell.field.fieldKey}`}
									aria-hidden="true"
									className={
										!singleColumn && cell.span < row.columns
											? cn(CELL_SPAN[cell.span], 'hidden sm:block')
											: 'hidden'
									}
								/>
							) : (
								<div
									key={
										cell.kind === 'field'
											? `field:${cell.field.fieldKey}`
											: `text:${cell.id}`
									}
									className={
										singleColumn ? CELL_SPAN[1] : CELL_SPAN[cell.span]
									}
								>
									{cell.kind === 'field'
										? renderField(
											cell.field,
											cell.optionColumns,
											!singleColumn && cell.span < row.columns,
										)
										: renderText(cell)}
								</div>
							),
						)}
					</div>
				));
				const header = hasHeader && (
					<div className={cn('border-b pb-2', colors.classes.borderDefault)}>
						{section.title != null && (
							<h2
								id={headingId}
								className={cn(
									'text-lg font-semibold',
									colors.classes.textPrimary,
								)}
							>
								{t(section.title)}
							</h2>
						)}
						{section.description != null && (
							<p
								className={cn(
									'whitespace-pre-line text-sm',
									section.title != null && 'mt-1',
									colors.classes.textSecondary,
								)}
							>
								{t(section.description)}
							</p>
						)}
					</div>
				);
				const spacing = cn(
					'space-y-5',
					sectionIndex > 0 && hasHeader && 'pt-4',
				);

				// A titled section is a landmark named by its heading; an untitled one (the
				// page without a layout) is only a group of rows.
				return section.title != null ? (
					<section
						key={section.id}
						aria-labelledby={headingId}
						className={spacing}
					>
						{header}
						{rows}
					</section>
				) : (
					<div key={section.id} className={spacing}>
						{header}
						{rows}
					</div>
				);
			})}
		</div>
	);
}

/**
 * PD-13768 — a locked value as the person reads it: a choice by its label, yes or no, a
 * date the way it is written, money as money.
 */
export function readOnlyText(field: FormFieldDefinitionModel, value: unknown): string {
	if (value == null || value === '') {
		return '';
	}
	if (field.fieldType === 'boolean' || typeof value === 'boolean') {
		return value === true ? 'Yes' : 'No';
	}
	if (field.fieldType === 'select' || field.fieldType === 'multiselect') {
		const labelOf = (choice: unknown): string =>
			field.selectOptions?.find((option) => option.value === String(choice))?.label ?? String(choice);
		return Array.isArray(value) ? value.map(labelOf).join(', ') : labelOf(value);
	}
	if (field.fieldType === 'currency' && Number.isFinite(Number(value))) {
		return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));
	}
	// The app's date style: two-digit years (M/D/YY, M/D/YY h:mm AM).
	if (field.fieldType === 'date' && typeof value === 'string') {
		return formatAgreementYmd(value);
	}
	if (field.fieldType === 'datetime' && typeof value === 'string') {
		return formatAgreementDateTime(value);
	}
	return Array.isArray(value) ? value.map(String).join(', ') : String(value);
}
